import { useCallback, useEffect, useState } from 'react';
import { Download, Search } from 'lucide-react';
import { formatDateTime, todayUk } from '@nu/shared';
import { api, ApiError } from '../api/client.js';

// Platform > Audit trail (admins): every recorded change across the platform,
// newest first: bookings (changes and notes), Match-Ups, vans, booking types
// access and page permissions. Read only.

const AREAS = [
  { value: '', label: 'Everything' },
  { value: 'bookings', label: 'Bookings' },
  { value: 'matchups', label: 'Match-Ups' },
  { value: 'vans', label: 'Vans' },
  { value: 'booking-types', label: 'Booking types' },
  { value: 'access', label: 'Access' },
  { value: 'permissions', label: 'Page access' },
];
const AREA_LABEL = Object.fromEntries(AREAS.map((a) => [a.value, a.label]));
const problem = (e) => (e?.code === 'offline' ? 'No connection.' : e instanceof ApiError ? e.message : 'Something went wrong. Try again.');

function daysAgo(n) {
  const d = new Date(`${todayUk()}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

// Excel-friendly CSV (UTF-8 with BOM, UK dates).
function downloadCsv(rows) {
  const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [['When', 'Area', 'Who', 'Reference', 'What'], ...rows.map((r) => [formatDateTime(r.at), AREA_LABEL[r.area], r.who, r.ref, r.what])];
  const blob = new Blob([`\uFEFF${lines.map((l) => l.map(cell).join(',')).join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `nano-utilities-audit-${todayUk()}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function AuditTrail() {
  const [area, setArea] = useState('');
  const [from, setFrom] = useState(() => daysAgo(30));
  const [to, setTo] = useState(todayUk);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [rows, setRows] = useState(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchPage = useCallback(async (offset) => {
    const p = new URLSearchParams({ offset: String(offset) });
    if (area) p.set('area', area);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (debouncedQ) p.set('q', debouncedQ);
    return api(`/audit?${p}`);
  }, [area, from, to, debouncedQ]);

  useEffect(() => {
    let live = true;
    setError(null);
    setRows(null);
    fetchPage(0).then((r) => { if (live) { setRows(r.entries); setMore(r.more); } }, (e) => { if (live) setError(problem(e)); });
    return () => { live = false; };
  }, [fetchPage]);

  const loadMore = async () => {
    setBusy(true);
    try {
      const r = await fetchPage(rows.length);
      setRows((x) => [...x, ...r.entries]);
      setMore(r.more);
    } catch (e) {
      setError(problem(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Audit trail</h1>
            <p className="muted">Every change recorded on the platform, newest first: bookings (including notes), Match-Ups, vans, booking types, access and page access. Each line is saved together with the change itself and can&apos;t be edited.</p>
          </div>
          <div className="head-actions">
            <button type="button" className="btn with-icon" disabled={!rows?.length} onClick={() => downloadCsv(rows)}><Download size={18} aria-hidden="true" /> Export CSV</button>
          </div>
        </div>
      </section>

      <section className="card">
        <div className="filters">
          <select value={area} onChange={(e) => setArea(e.target.value)} aria-label="Area">
            {AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <label className="day-pick inline"><span className="small muted">From</span><input type="date" className="filter-date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="day-pick inline"><span className="small muted">To</span><input type="date" className="filter-date" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></label>
          <label className="search">
            <Search size={18} aria-hidden="true" />
            <input type="search" placeholder="Search who, what or reference (NU-000123, a van reg…)" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
        </div>

        {error && <p className="notice-line notice-bad">{error}</p>}
        {!error && !rows && <p className="muted">Loading…</p>}
        {rows && !rows.length && <p className="muted">Nothing recorded for these filters.</p>}
        {rows?.length > 0 && (
          <div className="table-scroll">
            <table className="table roster audit">
              <thead><tr><th>When</th><th>Area</th><th>Who</th><th>Reference</th><th>What</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.at}-${i}`}>
                    <td data-label="When" className="nowrap">{formatDateTime(r.at)}</td>
                    <td data-label="Area"><span className="tag area-tag">{AREA_LABEL[r.area]}</span></td>
                    <td data-label="Who" className="small">{r.who}</td>
                    <td data-label="Reference">{r.ref ? <strong>{r.ref}</strong> : <span className="muted">–</span>}</td>
                    <td data-label="What" className="wrap">{r.what}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {more && <button type="button" className="btn" disabled={busy} onClick={loadMore}>{busy ? 'Loading…' : 'Show older'}</button>}
      </section>
    </div>
  );
}
